import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ActionButton, PageContainer, SurfaceCard } from '../ui/ConvivialComponents';
import TableListCard from '../ui/TableListCard';
import CityFilter from '../ui/CityFilter';
import { requestNativeLogin } from '../../services/nativeAppBridge';
import { getTableCity, isTableFull } from '../../utils/tablePresentation';

export default function IndexList({ pins }) {
    const navigate = useNavigate();
    const userId = localStorage.getItem('user_id');
    const [selectedCity, setSelectedCity] = useState('all');

    const tables = useMemo(
        () => Array.isArray(pins) ? pins.filter((pin) => pin.type === 'TABLE') : [],
        [pins]
    );
    const cities = useMemo(
        () => [...new Set(tables.map(getTableCity).filter(Boolean))]
            .sort((first, second) => first.localeCompare(second, 'it')),
        [tables]
    );
    const visibleTables = useMemo(() => {
        const tablesInSelectedCity = selectedCity === 'all'
            ? tables
            : tables.filter((table) => getTableCity(table) === selectedCity);

        return [...tablesInSelectedCity].sort(
            (first, second) => Number(isTableFull(first)) - Number(isTableFull(second))
        );
    }, [selectedCity, tables]);

    useEffect(() => {
        if (selectedCity !== 'all' && !cities.includes(selectedCity)) setSelectedCity('all');
    }, [cities, selectedCity]);

    const createTable = () => {
        if (!userId) {
            const handledByNative = requestNativeLogin('/table');

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo: '/table' } });
            }
            return;
        }
        navigate('/table');
    };

    return (
        <PageContainer >
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 32, sm: 40 } }}>
                        Tavoli vicino a te
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Scopri incontri conviviali e professionali nella tua zona.
                    </Typography>
                </Box>

                <CityFilter cities={cities} selectedCity={selectedCity} onChange={setSelectedCity} />

                {tables.length === 0 && (
                    <SurfaceCard>
                        <Typography variant="h3" sx={{ color: 'primary.main', mb: 1 }}>
                            Nessun tavolo trovato
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                            Puoi proporne uno tu e invitare altri professionisti.
                        </Typography>
                        <ActionButton onClick={createTable}>
                            {userId ? 'Crea un tavolo' : 'Accedi per creare'}
                        </ActionButton>
                    </SurfaceCard>
                )}

                {tables.length > 0 && visibleTables.length === 0 && (
                    <SurfaceCard variant="tonal">
                        <Typography variant="h3" sx={{ color: 'primary.main', mb: 1 }}>
                            Nessun tavolo attivo a {selectedCity}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Seleziona un'altra città oppure visualizza tutti i tavoli.
                        </Typography>
                    </SurfaceCard>
                )}

                {visibleTables.map((pin) => <TableListCard key={pin.id} table={pin} />)}
            </Stack>
        </PageContainer>
    );
}

